/**
 * WatchdogStatusPanel.jsx
 * 
 * Shows platform watchdog status: recent alerts, current health metrics,
 * manual trigger, and reset controls.
 * 
 * Place in: /src/components/WatchdogStatusPanel.jsx
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Shield, RefreshCw, Loader2, Trash2, Play, Clock,
  HardDrive, Key, Lock, CheckCircle, XCircle, AlertTriangle,
  Info, ChevronDown, ChevronRight
} from 'lucide-react';
import { toast } from 'react-toastify';

const EVENT_ICONS = {
  CERT_EXPIRY: Lock,
  DISK_USAGE: HardDrive,
  LICENSE_EXPIRY: Key,
  LICENSE_CAPACITY: Key,
};

const EVENT_LABELS = {
  CERT_EXPIRY: 'Certificate Expiry',
  DISK_USAGE: 'Disk Usage',
  LICENSE_EXPIRY: 'License Expiry',
  LICENSE_CAPACITY: 'License Capacity',
};

const SEVERITY_STYLES = {
  critical: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)' },
  warning: { color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
  info: { color: '#0ea5e9', bg: 'rgba(14,165,233,0.1)' },
};

const formatDate = (d) => {
  if (!d) return 'Never';
  return new Date(d).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const formatRelativeTime = (date) => {
  if (!date) return 'Never';
  const diffMs = Date.now() - new Date(date).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

const WatchdogStatusPanel = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await fetch('/api/platform/notifications/watchdog/status', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
      }
    } catch (err) {
      console.error('Failed to fetch watchdog status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const res = await fetch('/api/platform/notifications/watchdog/run', {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        toast.success('Watchdog checks started. Results will appear shortly.');
        setTimeout(fetchAlerts, 5000);
        setTimeout(fetchAlerts, 15000);
      } else {
        toast.error('Failed to trigger watchdog');
      }
    } catch {
      toast.error('Failed to trigger watchdog');
    } finally {
      setRunning(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Reset all watchdog alerts? This will allow all notifications to re-fire on the next check cycle.')) return;
    setResetting(true);
    try {
      const res = await fetch('/api/platform/notifications/watchdog/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      if (res.ok) {
        toast.success('All watchdog alerts reset');
        fetchAlerts();
      }
    } catch {
      toast.error('Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  // Group alerts by event type
  const grouped = {};
  alerts.forEach(a => {
    const type = a.eventType;
    if (!grouped[type]) grouped[type] = [];
    grouped[type].push(a);
  });

  return (
    <div className="wd-panel">
      <div className="wd-panel-header" onClick={() => setExpanded(!expanded)}>
        <div className="wd-panel-title">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <Shield size={16} color="#00d4aa" />
          <span>Platform Watchdog</span>
          <span className="wd-panel-subtitle">
            Certificate expiry, disk usage, license warnings
          </span>
        </div>
        <div className="wd-panel-actions" onClick={(e) => e.stopPropagation()}>
          <button className="btn btn-sm btn-secondary" onClick={handleRunNow} disabled={running}>
            {running ? <Loader2 size={13} className="spin" /> : <Play size={13} />}
            {running ? 'Running...' : 'Run Now'}
          </button>
          <button className="btn btn-sm btn-secondary" onClick={handleReset} disabled={resetting}>
            {resetting ? <Loader2 size={13} className="spin" /> : <Trash2 size={13} />}
            Reset
          </button>
          <button className="btn btn-sm btn-secondary" onClick={fetchAlerts} disabled={loading}>
            <RefreshCw size={13} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="wd-panel-body">
          {loading ? (
            <div className="wd-loading">
              <Loader2 size={18} className="spin" />
              <span>Loading watchdog status...</span>
            </div>
          ) : alerts.length === 0 ? (
            <div className="wd-empty">
              <CheckCircle size={20} color="#22c55e" />
              <span>No watchdog alerts. All platform health checks are passing.</span>
            </div>
          ) : (
            <div className="wd-alert-groups">
              {Object.entries(grouped).map(([type, items]) => {
                const Icon = EVENT_ICONS[type] || Info;
                const label = EVENT_LABELS[type] || type;
                return (
                  <div key={type} className="wd-alert-group">
                    <div className="wd-alert-group-header">
                      <Icon size={14} />
                      <span>{label}</span>
                      <span className="wd-alert-count">{items.length}</span>
                    </div>
                    <div className="wd-alert-items">
                      {items.map(alert => (
                        <div key={alert.id} className="wd-alert-item">
                          <div className="wd-alert-level">
                            {alert.level === 'expired' || alert.level === 'critical' || alert.level === 'full'
                              ? <XCircle size={12} color="#ef4444" />
                              : alert.level.includes('warning') || alert.level.includes('pct')
                              ? <AlertTriangle size={12} color="#f59e0b" />
                              : <Info size={12} color="#0ea5e9" />
                            }
                            <code className="wd-alert-level-tag">{alert.level}</code>
                          </div>
                          <span className="wd-alert-message">{alert.message}</span>
                          <span className="wd-alert-time">{formatRelativeTime(alert.sentAt)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WatchdogStatusPanel;
